import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, ShieldAlert, TriangleAlert } from "lucide-react";
import ParkFields, { validateParked, ParkedBadge } from "@/components/ParkFields";

const LEVELS = [1, 2, 3, 4, 5];
export function scoreColor(s) {
  if (s >= 15) return "#EF4444";
  if (s >= 10) return "#F97316";
  if (s >= 5) return "#F59E0B";
  return "#10B981";
}
function scoreLabel(s) {
  if (s >= 15) return "Critical";
  if (s >= 10) return "High";
  if (s >= 5) return "Moderate";
  return "Low";
}
const blank = { title: "", description: "", kind: "risk", probability: 3, impact: 3, status: "open", owner: "", mitigation: "", category: "", parked_reason: "", parked_revisit: "" };

export default function RiskRegister({ projectId }) {
  const [risks, setRisks] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/risks`);
      setRisks(data);
    } catch (e) { setRisks([]); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...blank, ...r }); setEditId(r.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!validateParked(form, toast.error)) return;
    setSaving(true);
    try {
      const payload = { ...form, probability: Number(form.probability), impact: Number(form.impact), parked_revisit: form.parked_revisit || null };
      if (editId) await api.put(`/risks/${editId}`, payload);
      else await api.post(`/projects/${projectId}/risks`, payload);
      setOpen(false);
      toast.success(editId ? "Updated" : "Logged");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/risks/${id}`);
    setOpen(false);
    toast.success("Deleted");
    load();
  };

  if (risks === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const cellRisks = (p, i) => risks.filter((r) => r.probability === p && r.impact === i && r.status !== "closed");

  const openList = risks.filter((r) => r.status !== "closed");
  const rstats = [
    { k: "risks", label: "Open risks", val: openList.filter((r) => r.kind !== "issue").length, color: "#DC2626", icon: ShieldAlert },
    { k: "issues", label: "Open issues", val: openList.filter((r) => r.kind === "issue").length, color: "#EA580C", icon: TriangleAlert },
    { k: "high", label: "High / Critical", val: openList.filter((r) => (r.score || 0) >= 10).length, color: "#F97316", icon: TriangleAlert },
    { k: "closed", label: "Closed", val: risks.filter((r) => r.status === "closed").length, color: "#2563EB", icon: ShieldAlert },
  ];

  return (
    <div className="space-y-5" data-testid="risk-register">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="risk-stats">
      {rstats.map((st) => (
        <div key={st.k} className="border border-border rounded-lg p-5" data-testid={`risk-kpi-${st.k}`}>
          <div className="flex items-center justify-between">
            <p className="overline text-muted-foreground">{st.label}</p>
            <st.icon className="h-4 w-4" style={{ color: st.color }} />
          </div>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: st.color }}>{st.val}</p>
        </div>
      ))}
    </div>
    <div className="grid lg:grid-cols-5 gap-6">
      {/* matrix */}
      <div className="lg:col-span-2 border border-border rounded-lg p-5 h-fit">
        <h3 className="font-display text-lg font-bold mb-1">Probability × Impact</h3>
        <p className="text-xs text-muted-foreground mb-4">Open risks & issues plotted by exposure.</p>
        <div className="flex">
          <div className="flex items-center">
            <span className="overline text-muted-foreground -rotate-90 whitespace-nowrap">Impact →</span>
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-5 gap-1">
              {[5, 4, 3, 2, 1].map((imp) =>
                LEVELS.map((prob) => {
                  const rs = cellRisks(prob, imp);
                  const s = prob * imp;
                  return (
                    <div key={`${prob}-${imp}`} className="aspect-square rounded flex items-center justify-center text-sm font-bold text-white/90" style={{ background: scoreColor(s), opacity: rs.length ? 1 : 0.28 }} title={`P${prob} × I${imp} = ${s}`} data-testid={`matrix-cell-${prob}-${imp}`}>
                      {rs.length || ""}
                    </div>
                  );
                })
              )}
            </div>
            <div className="text-center overline text-muted-foreground mt-2">Probability →</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 text-xs">
          {[["Low", 4], ["Moderate", 6], ["High", 12], ["Critical", 20]].map(([l, s]) => (
            <span key={l} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: scoreColor(s) }} />{l}</span>
          ))}
        </div>
      </div>

      {/* list */}
      <div className="lg:col-span-3 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Register <span className="text-sm text-muted-foreground font-normal">({risks.length})</span></h3>
          <Button className="gap-2" onClick={openNew} data-testid="add-risk-button"><Plus className="h-4 w-4" /> Log risk / issue</Button>
        </div>

        {risks.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-10 text-center">
            <ShieldAlert className="h-8 w-8 text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">No risks or issues logged yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {risks.map((r) => (
              <button key={r.id} onClick={() => openEdit(r)} className="w-full border border-border rounded-lg p-4 text-left hover:border-foreground/30 transition-colors" data-testid={`risk-card-${r.id}`}>
                <div className="flex items-start gap-3">
                  {r.kind === "issue" ? <TriangleAlert className="h-4 w-4 text-destructive mt-0.5 shrink-0" /> : <ShieldAlert className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{r.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{r.mitigation || r.description || "No mitigation plan yet."}</p>
                    <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                      <span className="uppercase tracking-wide font-semibold">{r.kind}</span>
                      {r.owner && <span>· {r.owner}</span>}
                      <span>· {r.status === "parked" ? "on hold / parked" : r.status}</span>
                      <ParkedBadge item={r} testid={`risk-parked-${r.id}`} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="inline-flex items-center justify-center h-7 min-w-7 px-2 rounded-md text-xs font-bold text-white" style={{ background: scoreColor(r.score) }}>{r.score}</span>
                    <p className="text-[10px] text-muted-foreground mt-1">{scoreLabel(r.score)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="risk-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Log"} risk / issue</DialogTitle>
            <DialogDescription>Rate probability and impact (1–5); exposure score updates automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="r-title">Title</Label>
              <Input id="r-title" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="risk-title-input" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="risk-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="risk">Risk</SelectItem><SelectItem value="issue">Issue</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="risk-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="mitigating">Mitigating</SelectItem>
                    <SelectItem value="parked">On Hold / Parked</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Probability (1–5)</Label>
                <Select value={String(form.probability)} onValueChange={(v) => set("probability", Number(v))}>
                  <SelectTrigger className="mt-1.5" data-testid="risk-prob-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={String(l)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Impact (1–5)</Label>
                <Select value={String(form.impact)} onValueChange={(v) => set("impact", Number(v))}>
                  <SelectTrigger className="mt-1.5" data-testid="risk-impact-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={String(l)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <ParkFields form={form} set={set} testPrefix="risk" />
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Exposure score:</span>
              <span className="inline-flex items-center justify-center h-6 px-2 rounded text-xs font-bold text-white" style={{ background: scoreColor(form.probability * form.impact) }}>{form.probability * form.impact} · {scoreLabel(form.probability * form.impact)}</span>
            </div>
            <div>
              <Label htmlFor="r-owner">Owner</Label>
              <Input id="r-owner" value={form.owner} onChange={(e) => set("owner", e.target.value)} placeholder="Who owns this?" data-testid="risk-owner-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="r-desc">Description</Label>
              <Textarea id="r-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="risk-desc-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="r-mit">Mitigation plan</Label>
              <Textarea id="r-mit" rows={2} value={form.mitigation} onChange={(e) => set("mitigation", e.target.value)} placeholder="How will you respond?" data-testid="risk-mitigation-input" className="mt-1.5" />
            </div>
            <DialogFooter className="flex sm:justify-between gap-2">
              {editId && (
                <Button type="button" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 gap-2" onClick={() => remove(editId)} data-testid="risk-delete-button">
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              )}
              <Button type="submit" disabled={saving} data-testid="risk-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
